import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  Mail,
} from "lucide-react";
import ServiceDeskAPIService from "@sd/services/APIs";
import { useAuth } from "@sd/hooks/useAuth";
import {
  isValidAccessToken,
  notifySdAuthChanged,
  extractAuthTokens,
  extractAuthUser,
} from "@sd/lib/sdAuthSession";
import {
  resolveSdPostLoginPath,
} from "@sd/lib/sdReturnPath";
import { isSdAppRole } from "@sd/lib/sdRoles";
import { sd, type SdAppRole } from "@sd/lib/sdPaths";
import {
  setLocalAccessToken,
  setLocalRefreshToken,
  setLocalUserInfo,
} from "@/lib/storeManager";
import {
  SdAuthShell,
  sdAuthInputClassName,
  sdAuthLabelClassName,
  sdAuthPrimaryButtonClassName,
} from "@sd/components/auth/SdAuthShell";
import { parseSdAuthApiError } from "@sd/components/auth/parseSdAuthApiError";

type AuthBody = Record<string, unknown>;

function resolveSdRoleFromUserRaw(userRaw: unknown): SdAppRole | null {
  if (!userRaw || typeof userRaw !== "object") return null;
  const u = userRaw as Record<string, unknown>;
  const candidates = [u.role, u.service_desk_role, u.sd_role];
  for (const c of candidates) {
    const s = String(c ?? "").trim();
    if (isSdAppRole(s)) {
      return s;
    }
  }
  return null;
}

function extractTokens(body: AuthBody): {
  access: string | null;
  refresh: string | null;
  userRaw: unknown;
} {
  const { access, refresh } = extractAuthTokens(body);
  return { access, refresh, userRaw: extractAuthUser(body) };
}

export default function SDLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { refetchRole, role, isLoading: authLoading } = useAuth();

  useEffect(() => {
    if (!isValidAccessToken() || authLoading) return;
    navigate(
      resolveSdPostLoginPath(role, location.state?.from),
      { replace: true }
    );
  }, [navigate, location.state, role, authLoading]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const applySessionFromBody = async (body: AuthBody): Promise<boolean> => {
    const { access, refresh, userRaw } = extractTokens(body);
    if (!access || !refresh) {
      return false;
    }
    setLocalAccessToken(access);
    setLocalRefreshToken(refresh);
    if (userRaw && typeof userRaw === "object") {
      const u = userRaw as Record<string, unknown>;
      setLocalUserInfo({
        ...u,
        id: (u.id ?? u.user_id) as string | undefined,
      });
    }
    notifySdAuthChanged();
    const loginRole = resolveSdRoleFromUserRaw(userRaw);
    await refetchRole();
    queryClient.clear();
    navigate(
      resolveSdPostLoginPath(loginRole ?? role, location.state?.from),
      { replace: true }
    );
    return true;
  };

  const parseApiError = (err: unknown): string => parseSdAuthApiError(err);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await ServiceDeskAPIService.auth.login({ email, password });
      const body = response.data as AuthBody;
      const ok = await applySessionFromBody(body);
      if (!ok) {
        setError("Login failed. Please check your credentials.");
      }
    } catch (err: unknown) {
      setError(parseApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <SdAuthShell>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-900">Welcome back</h2>
        <p className="mt-1 text-sm text-slate-500">
          Sign in to your account
        </p>
      </div>

      {error && (
        <div
          className="mb-5 flex gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3.5 text-sm text-red-800"
          role="alert"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" aria-hidden />
          <p className="leading-relaxed">{error}</p>
        </div>
      )}

      <form onSubmit={handleLogin} className="space-y-5">
        <div>
          <label htmlFor="sd-email" className={sdAuthLabelClassName}>
            Email address
          </label>
          <div className="relative">
            <Mail
              className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400"
              strokeWidth={1.75}
              aria-hidden
            />
            <input
              id="sd-email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="name@company.com"
              className={sdAuthInputClassName}
            />
          </div>
        </div>

        <div>
          <label htmlFor="sd-password" className={sdAuthLabelClassName}>
            Password
          </label>
          <div className="relative">
            <Lock
              className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400"
              strokeWidth={1.75}
              aria-hidden
            />
            <input
              id="sd-password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              placeholder="Enter your password"
              className={`${sdAuthInputClassName} pr-12`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(p => !p)}
              className="absolute right-1 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <EyeOff className="h-[18px] w-[18px]" strokeWidth={1.75} />
              ) : (
                <Eye className="h-[18px] w-[18px]" strokeWidth={1.75} />
              )}
            </button>
          </div>
        </div>

        <button type="submit" disabled={loading} className={sdAuthPrimaryButtonClassName}>
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Signing in…
            </>
          ) : (
            "Sign In"
          )}
        </button>
      </form>

      <p className="mt-5 text-center">
        <Link
          to={sd("forgot-password")}
          className="text-sm font-semibold text-blue-600 hover:text-blue-700"
        >
          Forgot Password?
        </Link>
      </p>
    </SdAuthShell>
  );
}
